import { useCallback } from 'react'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { PosicionPublica, RondaPublica, TorneoPublico } from '../datos/index.js'
import { conSigno, cuantosCompiten, signoDe } from '../formato.js'
import { ESPERA_CACHE_PUBLICA_MS, useAvisosDelTorneo, useLlamadaEspaciada } from '../tiempo-real.js'
import { Bandera } from './Bandera.js'
import { Cargando } from './Estados.js'
import { Icono } from './Icono.js'
import { MesaVs } from './MesaVs.js'
import { PastillaVitrina } from './Pastilla.js'

/**
 * Las piezas de la vitrina, la parte de la web que se ve sin cuenta.
 *
 * Las usan las dos pantallas públicas: la portada, que muestra todos los
 * torneos, y la de un torneo. Están acá y no repetidas porque son las mismas
 * respuestas a la misma pregunta («¿cómo va?») en dos tamaños.
 */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

/** Cómo se llama cada modalidad para el que mira. */
export const MODALIDAD: Record<string, string> = {
  individual: 'Individual',
  parejas: 'Parejas',
  equipos: 'Por equipos',
}

/**
 * La ronda que vale la pena mirar: la que se juega, o si no hay ninguna abierta,
 * la última que tuvo mesas. Es el mismo criterio de la pestaña de mesas.
 */
export function rondaDelMomento(rondas: readonly RondaPublica[]): RondaPublica | null {
  const conMesas = rondas.filter((r) => r.mesas > 0)
  return conMesas.find((r) => r.estado !== 'cerrada') ?? conMesas[conMesas.length - 1] ?? null
}

/** Cuántas mesas mostrar en la tira: dos pantallas de pasar el dedo, no cien. */
const MESAS_EN_TIRA = 8

/** Cada cuánto, como mucho, se vuelven a pedir las mesas de la tira por avisos. */
const FRENO_TIRA_MS = 5_000

/**
 * Las mesas de la ronda del momento, en una tira que se pasa de costado.
 *
 * Las terminadas primero: son las que tienen algo que contar. Las que se juegan
 * vienen después con su «Jugando», y la mesa incompleta no sale porque no hay
 * enfrentamiento que mostrar.
 *
 * Pide sus mesas aparte de la pantalla que la contiene. Así la portada y el
 * resumen del torneo se dibujan con lo liviano (la lista, la tabla) y las mesas
 * llegan un momento después, en su lugar.
 *
 * Se mantiene al día con los avisos del socket, con el mismo freno que la
 * pestaña «Mesas» de la vitrina (ver `MesasDeRonda`).
 */
export function MesasDestacadas({
  torneoId,
  rondas,
  alVerTodas,
  alActualizar,
}: {
  torneoId: string
  rondas: RondaPublica[]
  alVerTodas: () => void
  /** Volver a pedir todo: los marcadores cambian mientras se mira. */
  alActualizar?: () => void
}) {
  const ronda = rondaDelMomento(rondas)
  const rondaId = ronda?.rondaId ?? null

  const pedir = useCallback(async () => {
    if (!rondaId) return []
    const repo = await obtenerRepositorio()
    return repo.mesasDeRondaPublicas(torneoId, rondaId)
  }, [torneoId, rondaId])

  const { datos, refrescar } = useCarga(pedir)

  const pronto = useLlamadaEspaciada(refrescar, FRENO_TIRA_MS, ESPERA_CACHE_PUBLICA_MS)
  useAvisosDelTorneo(torneoId, true, (aviso) => {
    if ((aviso.tipo === 'mesa' || aviso.tipo === 'ronda') && aviso.rondaId !== rondaId) return
    pronto()
  })

  if (!ronda) return null

  const mesas = (datos ?? [])
    .filter((m) => m.tipo !== 'incompleta')
    .sort((a, z) => Number(z.marcador !== null) - Number(a.marcador !== null) || a.numero - z.numero)
    .slice(0, MESAS_EN_TIRA)

  return (
    <section className="seccion vitrina-mesas">
      <div className="seccion-cabecera">
        <h3 className="seccion-titulo">
          Ronda {ronda.numero}
          <span className="seccion-dato">
            {ronda.mesasCargadas} de {ronda.mesas} terminadas
          </span>
        </h3>
        <span className="seccion-acciones">
          {alActualizar && (
            <button
              className="boton-icono"
              onClick={alActualizar}
              title="Actualizar"
              aria-label="Actualizar"
            >
              <Icono nombre="refrescar" tamano={16} />
            </button>
          )}
          <button className="enlace" onClick={alVerTodas}>
            Ver todas
            <Icono nombre="flecha" tamano={15} />
          </button>
        </span>
      </div>

      {!datos ? (
        <Cargando que="Trayendo las mesas…" />
      ) : (
        <div className="vs-tira" role="list">
          {mesas.map((m) => (
            <div role="listitem" key={m.mesaId}>
              <MesaVs mesa={m} alTocar={alVerTodas} />
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * Los cinco de arriba de la tabla.
 *
 * Es el «¿y quién va primero?» contestado sin la tabla entera. Sin ninguna ronda
 * cerrada están todos en cero y el orden es el del sorteo, así que ahí no se
 * muestra: sería un podio de mentira.
 */
export function Punteros({
  posiciones,
  titulo,
  alVerTabla,
}: {
  posiciones: PosicionPublica[]
  titulo: string
  alVerTabla: () => void
}) {
  const arriba = posiciones.slice(0, 5)
  if (arriba.length === 0) return null

  return (
    <section className="seccion vitrina-punteros">
      <div className="seccion-cabecera">
        <h3 className="seccion-titulo">{titulo}</h3>
        <button className="enlace" onClick={alVerTabla}>
          Tabla completa
          <Icono nombre="flecha" tamano={15} />
        </button>
      </div>
      <ol className="resumen-podio">
        {arriba.map((p) => (
          <li key={`${p.participanteId}-${p.torneoJugadorId ?? ''}`}>
            <span className={`resumen-puesto ${p.posicion <= 3 ? 'podio' : ''}`}>{p.posicion}</span>
            <Bandera codigo={p.pais} />
            <span className="acortar">{p.nombre}</span>
            <span className="resumen-cifras">
              <span className="ganadas">{p.ganadas}G</span>
              <span className="perdidas">{p.perdidas}P</span>
              <span className={`efectividad ${signoDe(p.efectividad)}`}>
                {conSigno(p.efectividad)}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

const METALES = ['oro', 'plata', 'bronce'] as const

/**
 * El podio de un torneo terminado, con el primero en el medio y más alto.
 *
 * Es la foto con la que el torneo queda: el orden de los escalones es el de una
 * premiación (2, 1, 3) y no el de la lista, porque así se reconoce de un vistazo.
 * En el teléfono angosto sigue siendo de tres columnas: con nombres cortos
 * entran, y apilado dejaría de parecer un podio.
 */
export function PodioFinal({ posiciones }: { posiciones: PosicionPublica[] }) {
  const tres = posiciones.slice(0, 3)
  if (tres.length === 0) return null

  // 2º, 1º, 3º: el orden de los escalones.
  const escalones = [tres[1], tres[0], tres[2]].filter((p): p is PosicionPublica => !!p)

  return (
    <ol className="podio-final" aria-label="Podio">
      {escalones.map((p) => {
        const metal = METALES[p.posicion - 1] ?? 'bronce'
        return (
          <li key={p.participanteId} className={`escalon ${metal}`}>
            <span className="escalon-medalla">
              <Icono nombre="medalla" tamano={p.posicion === 1 ? 30 : 24} />
            </span>
            <span className="escalon-nombre" title={p.nombre}>
              <Bandera codigo={p.pais} />
              <span>{p.nombre}</span>
            </span>
            <span className="escalon-cifras">
              {p.ganadas}G · <span className={signoDe(p.efectividad)}>{conSigno(p.efectividad)}</span>
            </span>
            <span className="escalon-base">{p.posicion}º</span>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Un torneo en una lista de la portada.
 *
 * Con la fecha en un almanaque a la izquierda: en la lista de lo que viene es lo
 * primero que se busca, y en la de terminados es lo que distingue al de este
 * domingo del de hace un mes con el mismo nombre.
 */
export function TarjetaTorneo({
  torneo,
  propio = false,
  alTocar,
}: {
  torneo: TorneoPublico
  /** Si lo juega el que mira, que tiene sesión. */
  propio?: boolean
  alTocar: () => void
}) {
  const [, mes, dia] = torneo.fecha.split('-')

  return (
    <button className="tarjeta-torneo" onClick={alTocar}>
      <span className="almanaque" aria-hidden="true">
        <b>{Number(dia)}</b>
        <span>{MESES[Number(mes) - 1] ?? ''}</span>
      </span>
      <span className="tarjeta-torneo-texto">
        <b className="acortar">{torneo.nombre}</b>
        <span className="tenue acortar">
          {torneo.organizacion} · {MODALIDAD[torneo.modalidad] ?? torneo.modalidad}
        </span>
        <span className="tarjeta-torneo-pie">
          <PastillaVitrina estado={torneo.estado} />
          {propio && (
            <span className="pastilla info">{torneo.estado === 'finalizado' ? 'Jugaste' : 'Juegas'}</span>
          )}
          <span className="tenue">{cuantosCompiten(torneo.participantes, torneo.modalidad)}</span>
        </span>
      </span>
      <Icono nombre="flecha" tamano={18} />
    </button>
  )
}
